import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isDesignModeActive, useDesignMode } from '@/features/designMode/designModeStore'
import { PREVIEW_TRANSITION_MS } from './documentPreviewConstants.js'
import { resolveArtifactForPreview } from './artifactPayloadStore.js'
import { DocumentPreviewContext } from './documentPreviewContextValue.js'
import { onDocumentPreviewOpenRequested } from './documentPreviewEvents.js'

export function DocumentPreviewProvider({ children }) {
  const designMode = useDesignMode()
  const [activeArtifact, setActiveArtifact] = useState(
    /** @type {import('./documentPreviewTypes.js').GeneratedArtifactPreview | null} */ (null),
  )
  const [isExiting, setIsExiting] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const exitTimerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null))

  const clearExitTimer = useCallback(() => {
    if (exitTimerRef.current != null) {
      clearTimeout(exitTimerRef.current)
      exitTimerRef.current = null
    }
  }, [])

  const openPreview = useCallback(
    (selection) => {
      clearExitTimer()
      setIsExiting(false)
      setIsExpanded(false)
      const stored = resolveArtifactForPreview(selection)
      const focusSectionId = selection.focusSectionId ?? null
      const focusPage = selection.focusPage ?? null
      if (stored) {
        setActiveArtifact({ ...stored, focusSectionId, focusPage })
        return
      }
      setActiveArtifact({
        artifactId: selection.artifactId,
        kind: selection.kind,
        fileName: selection.fileName,
        description: selection.description,
        focusSectionId,
        focusPage,
        payload:
          selection.kind === 'spreadsheet'
            ? { columns: [], rows: [] }
            : selection.kind === 'html'
              ? { html: '' }
              : selection.kind === 'pdf'
                ? { url: '' }
                : { sections: [] },
      })
    },
    [clearExitTimer],
  )

  const expandPreview = useCallback(() => {
    if (!activeArtifact) return
    setIsExpanded(true)
  }, [activeArtifact])

  const collapsePreview = useCallback(() => {
    setIsExpanded(false)
  }, [])

  const closePreview = useCallback(() => {
    if (isDesignModeActive()) return
    if (!activeArtifact) return
    clearExitTimer()
    setIsExpanded(false)
    setIsExiting(true)
    exitTimerRef.current = setTimeout(() => {
      setActiveArtifact(null)
      setIsExiting(false)
      exitTimerRef.current = null
    }, PREVIEW_TRANSITION_MS)
  }, [activeArtifact, clearExitTimer])

  useEffect(() => {
    return () => clearExitTimer()
  }, [clearExitTimer])

  useEffect(() => {
    return onDocumentPreviewOpenRequested((selection) => {
      openPreview(selection)
    })
  }, [openPreview])

  const value = useMemo(
    () => ({
      isOpen: Boolean(designMode || (activeArtifact != null && !isExiting)),
      isExiting,
      isExpanded,
      activeArtifact,
      openPreview,
      closePreview,
      expandPreview,
      collapsePreview,
    }),
    [activeArtifact, designMode, isExiting, isExpanded, openPreview, closePreview, expandPreview, collapsePreview],
  )

  return <DocumentPreviewContext.Provider value={value}>{children}</DocumentPreviewContext.Provider>
}
